import React, { useContext } from 'react';
import CompanySectionLayout from '../../../../component/B2B/CompanySectionLayout';
import { SidebarContext } from '../../../../context/SidebarContext';
import { getLocalStorageItem } from '../../../../utils/helper';

/**
 * Company panel → Scans: the three camera-based self-checks in the app.
 *
 *   Overview    how many people photograph themselves for a health check at all
 *   Skin Scan   a face scan, or a close-up of one patch of skin
 *   Teeth Scan  six photos of the mouth, plus a short dental questionnaire
 *   Age Scan    a face photo read against the person's date of birth
 *
 * WHAT THIS SECTION WILL NEVER SHOW: what a scan found. No skin score or risk
 * level, no hygiene score or band, no facial age, no wellbeing reading, no
 * photo and no report. Those are health findings about an identifiable person,
 * they were addressed to that person, and no endpoint behind these pages reads
 * them.
 *
 * What the section is for instead: take-up, whether people come back, and
 * whether the analysis actually works. For a feature that runs an expensive AI
 * model over an upload, "how often does this fail and how long does it take"
 * is most of what an admin needs, and it costs nobody their medical privacy.
 */
const CompanyScansLayout = ({ children }) => {
  const { isB2BFansMode } = useContext(SidebarContext);
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const population = isB2BFansMode ? 'members' : 'employees';
  const base = `/${slug}/scans`;

  const pages = [
    {
      label: 'Overview',
      path: base,
      description: 'Take-up across all three scans, and how reliably the analysis finishes'
    },
    {
      label: 'Skin Scan',
      path: `${base}/skin`,
      description: 'Face and skin-area checks, and how complete each submission is'
    },
    {
      label: 'Teeth Scan',
      path: `${base}/teeth`,
      description: 'Which of the six photos people manage, and what they say about their habits'
    },
    {
      label: 'Age Scan',
      path: `${base}/age`,
      description: 'Take-up, repeat use, and whether the analysis returns a full result'
    },
    {
      label: isB2BFansMode ? 'Members' : 'Employees',
      path: `${base}/users`,
      description: `Scan history per ${population.replace(
        /s$/,
        ''
      )} — dates and outcomes, never results`
    }
  ];

  return (
    <CompanySectionLayout
      title="Scans"
      subtitle={`How many of your ${population} use the Skin, Teeth and Age scans — and whether the scans work. Results stay with the person who took them.`}
      pages={pages}
    >
      {children}
    </CompanySectionLayout>
  );
};

export default CompanyScansLayout;
